import { FC, useMemo, useState } from 'react';
import useHome from '@/hooks/use-home.hook';
import TileGroup from '@/components/tile-group';
import TileGroupHeader from '@/components/tile-group/header';
import TileContent from '@/components/tile-group/content';
import EmptyTile from '@/components/tile-group/empty-tile';
import { UiOption } from '@/lib/helpers';
import { TILE_COMPONENTS } from '@/components/tile-group/tile/helpers';

type QuickFilterId = 'agent' | 'public-service';

const AllTileGroup: FC = () => {
  const { tiles, hasAgents, hasServices } = useHome();
  const [keyword, setKeyword] = useState('');
  const [quickFilters, setQuickFilters] = useState<QuickFilterId[]>([]);

  const handleChangeQuickFilters = (filterId: QuickFilterId, enabled: boolean) => {
    setQuickFilters((old) => {
      if (enabled) {
        return [...old, filterId];
      }
      return old.filter((val) => val !== filterId);
    });
  };

  const quickFilterOptions = useMemo(() => {
    const options: UiOption[] = [];

    if (hasAgents) {
      options.push({ label: 'Assistants', value: 'agent' });
    }

    if (hasServices) {
      options.push({ label: 'Apps', value: 'public-service' });
    }

    return options;
  }, [hasAgents, hasServices]);

  const filterTiles = useMemo(() => {
    const safeKeyword = String(keyword).trim().toLowerCase();

    return tiles.filter(({ keywords, tileType }) => {
      if (!['agent', 'surepath', 'public-service'].includes(tileType)) {
        return false;
      }

      if (safeKeyword) {
        // keyword filter
        const keywordMatch = keywords.some((field) =>
          String(field).toLowerCase().includes(safeKeyword)
        );

        if (!keywordMatch) {
          return false;
        }
      }

      // quick filters
      if (quickFilters.length) {
        const filtersMatch = quickFilters.some((filter) => {
          switch (filter) {
            case 'agent':
              return tileType === 'agent';

            case 'public-service':
              return tileType === 'public-service';
          }
        });

        if (!filtersMatch) {
          return false;
        }
      }

      return true;
    });
  }, [tiles, keyword, quickFilters]);

  const showEmptyTile = !!keyword && !filterTiles.length;

  return (
    <TileGroup>
      <TileGroupHeader
        title="All Available"
        icon="cloud"
        onSearch={setKeyword}
        onChangeQuickFilters={handleChangeQuickFilters}
        quickFilters={quickFilters}
        quickFilterOptions={quickFilterOptions}
      />
      <TileContent>
        {filterTiles.map((tile) => {
          const TileEl = TILE_COMPONENTS[tile.tileType];

          if (TileEl) {
            return <TileEl key={tile.tileId} tile={tile} />;
          }
        })}
        {showEmptyTile && <EmptyTile />}
      </TileContent>
    </TileGroup>
  );
};

export default AllTileGroup;
